import { Component, ReactNode } from 'react'
import * as styles from 'components/features/pfm/ModuleWrapper/ModuleWrapper.css'
import { connect } from 'react-redux'
import { State } from 'interfaces/state'
import { AnchorPlacement } from 'interfaces/navigation'
import InsightsContainer from 'components/features/insights/Insights/InsightsContainer'
import { CardBlock } from 'components/features/pfm/Blocks/Blocks'
import { hasAnchorInsights } from 'helpers/insightHelpers'
import Anchor from 'components/atoms/navigation/Anchor'
import { BlockCorners, QAMarked, ShadowTheme } from 'interfaces/component'
import { blockCornersSelector } from 'selectors/globalSelectors'
import ModuleHeader, { ModuleHeaderProps } from 'components/features/pfm/ModuleWrapper/ModuleHeader'
import * as cn from 'classnames'

type ModuleWrapperOwnProps = QAMarked & {
  title?: ReactNode
  subtitle?: string
  hint?: ReactNode
  titleLinkTo?: AnchorPlacement
  action?: ReactNode
  anchor?: AnchorPlacement
  card?: boolean
  expand?: boolean
  noPadding?: boolean
  isFlexLayout?: boolean
  corners?: BlockCorners
  shadowTheme?: ShadowTheme
  nestedModule?: boolean
  forceRoundedCorners?: boolean
}

type ModuleWrapperStateProps = {
  shouldRenderInsights: boolean
  corners: BlockCorners
}

type ModuleWrapperProps = ModuleWrapperOwnProps & ModuleWrapperStateProps

class ModuleWrapper extends Component<ModuleWrapperProps, {}> {
  static defaultProps: Partial<ModuleWrapperProps> = {
    card: true,
  }

  render () {
    const {
      noPadding,
      isFlexLayout,
      qaType,
      qaMarker,
    } = this.props

    const className = cn(styles.moduleWrapper, {
      [styles.noPadding]: noPadding,
      [styles.flexLayout]: isFlexLayout,
    })

    return (
      <div
        className={ className }
        data-qa-type={ qaType }
        data-qa-marker={ qaMarker }
      >
        { this.renderChildren() }
      </div>
    )
  }

  renderChildren () {
    const {
      anchor,
      title,
      subtitle,
      action,
      titleLinkTo,
      hint,
      noPadding,
    } = this.props

    const moduleHeaderProps: ModuleHeaderProps = {
      hint,
      title,
      subtitle,
      action,
      titleLinkTo,
      noPadding,
    }

    const children = (
      <>
        <ModuleHeader { ...moduleHeaderProps } />
        { this.renderContent() }
        { this.renderInsights() }
      </>
    )

    return anchor
      ? (
        <Anchor placement={ anchor }>
          { children }
        </Anchor>
      )
      : children
  }

  renderContent () {
    const {
      card,
      corners,
      expand,
      noPadding,
      shadowTheme,
      forceRoundedCorners,
      children,
    } = this.props

    if (!card) {
      return children
    }

    return (
      <CardBlock
        corners={ corners }
        isExpanded={ expand }
        withPadding={ !noPadding }
        shadowTheme={ shadowTheme }
        forceRoundedCorners={ forceRoundedCorners }
      >
        { children }
      </CardBlock>
    )
  }

  renderInsights () {
    const {
      shouldRenderInsights,
      anchor,
    } = this.props

    if (!shouldRenderInsights || !anchor || !hasAnchorInsights(anchor)) {
      return null
    }

    return (
      <InsightsContainer placement={ anchor } />
    )
  }
}

const mapStateToProps = (state: State, ownProps: ModuleWrapperOwnProps): ModuleWrapperStateProps => {
  const { app } = state
  const { anchor, corners } = ownProps

  const moduleInsights = anchor && hasAnchorInsights(anchor) ? app.insights.order.byPlacement[anchor] : undefined
  const hasInsights = moduleInsights ? moduleInsights.length > 0 : false
  const shouldRenderInsights = hasInsights || app.insights.firstInsightsLoading

  const resultCorners = corners
    ? corners
    : blockCornersSelector(state, ownProps)

  return {
    shouldRenderInsights,
    corners: resultCorners === 'all' && shouldRenderInsights ? 'top' : resultCorners,
  }
}

export default connect(mapStateToProps)(ModuleWrapper)
